import { createContext } from '@direct-frontend/modules-context';
import type {
    OpenApiSchemaNewProgramProgress,
    OpenApiSchemaProgramType,
    OpenApiSchemaStoreProgramAdSpent,
} from '../../api/schema';
import { getLocalExperimentalFlag } from '../flags';

export interface StoreProgramContext {
    // Текущая программа магазина
    currentProgram: OpenApiSchemaProgramType;
    spendingProgress: OpenApiSchemaStoreProgramAdSpent;
    // Показывать старую программу, если первое подключение было до 04.07.2026
    showOldProgram: boolean;
}

export const DEFAULT_STORE_PROGRAM: OpenApiSchemaProgramType = 'NONE';

export const DEFAULT_SHOW_OLD_PROGRAM = false;

export const DEFAULT_SPENDING_PROGRESS: OpenApiSchemaStoreProgramAdSpent = {
    spent_last_month: null,
    spent_current_month: null,
    goal_value: null,
    yandex_direct_balance: null,
    goal_reached_last_month: false,
    goal_reached_current_month: false,
    need_yandex_direct_balance_alert: false,
};

export const { setContext: setStoreProgramContext, getContext: getStoreProgramContext } =
    createContext<StoreProgramContext>({
        name: 'storeProgramContext',
    });

function parseSpentOverride(value: string | null): number | null {
    if (value === null || value === '') {
        return null;
    }

    const num = Number(value);

    return Number.isFinite(num) ? num : null;
}

function parseGoalReachedOverride(value: string | null): boolean {
    return value === '1';
}

function applySpendingOverrides(
    spendingProgress: OpenApiSchemaStoreProgramAdSpent,
    forceNewProgramProgress: boolean
): OpenApiSchemaStoreProgramAdSpent {
    const spentLastMonth = getLocalExperimentalFlag({ name: 'override_spent_last_month' });
    const spentCurrentMonth = getLocalExperimentalFlag({ name: 'override_spent_current_month' });
    const goalReachedLastMonth = getLocalExperimentalFlag({ name: 'override_goal_reached_last_month' });
    const goalReachedCurrentMonth = getLocalExperimentalFlag({ name: 'override_goal_reached_current_month' });
    const goalValue = getLocalExperimentalFlag({ name: 'goal_value' });
    const yandexDirectBalance = getLocalExperimentalFlag({ name: 'yandex_direct_balance' });
    const needYandexDirectBalanceAlert = getLocalExperimentalFlag({ name: 'need_yandex_direct_balance_alert' });
    const alertPeriod = getLocalExperimentalFlag({
        name: 'alert_period',
    }) as OpenApiSchemaNewProgramProgress['alert_period'] | null;
    const coInvestPercent = getLocalExperimentalFlag({ name: 'co_invest_percent' });

    const spendingProgressWithOverrides: OpenApiSchemaStoreProgramAdSpent = {
        ...spendingProgress,
        ...(spentLastMonth != null && { spent_last_month: parseSpentOverride(spentLastMonth) }),
        ...(spentCurrentMonth != null && { spent_current_month: parseSpentOverride(spentCurrentMonth) }),
        ...(goalReachedLastMonth != null && {
            goal_reached_last_month: parseGoalReachedOverride(goalReachedLastMonth),
        }),
        ...(goalReachedCurrentMonth != null && {
            goal_reached_current_month: parseGoalReachedOverride(goalReachedCurrentMonth),
        }),
        ...(goalValue != null && { goal_value: parseSpentOverride(goalValue) }),
        ...(yandexDirectBalance != null && { yandex_direct_balance: yandexDirectBalance }),
        ...(needYandexDirectBalanceAlert != null && {
            need_yandex_direct_balance_alert: parseGoalReachedOverride(needYandexDirectBalanceAlert),
        }),
    };

    const currentNewProgramProgress = spendingProgressWithOverrides.new_program_progress;

    if (!currentNewProgramProgress && !forceNewProgramProgress) {
        return spendingProgressWithOverrides;
    }

    const newProgramProgress: OpenApiSchemaNewProgramProgress = {
        spent_percent: currentNewProgramProgress?.spent_percent ?? null,
        goal_reached: currentNewProgramProgress?.goal_reached ?? false,
        goal_value: currentNewProgramProgress?.goal_value ?? null,
        alert_period: currentNewProgramProgress?.alert_period ?? 'NONE',
        co_invest_percent: currentNewProgramProgress?.co_invest_percent ?? null,
    };

    return {
        ...spendingProgressWithOverrides,
        new_program_progress: {
            ...newProgramProgress,
            ...(spentCurrentMonth != null && {
                spent_percent: parseSpentOverride(spentCurrentMonth),
            }),
            ...(goalReachedCurrentMonth != null && {
                goal_reached: parseGoalReachedOverride(goalReachedCurrentMonth),
            }),
            ...(goalValue != null && {
                goal_value: parseSpentOverride(goalValue),
            }),
            ...(alertPeriod != null && { alert_period: alertPeriod }),
            ...(coInvestPercent != null && {
                co_invest_percent: parseSpentOverride(coInvestPercent),
            }),
        },
    };
}

export const getSafeStoreProgramContext = (): StoreProgramContext => {
    const programOverride = getLocalExperimentalFlag({
        name: 'override_program',
    }) as OpenApiSchemaProgramType;
    const programVersionOverride = getLocalExperimentalFlag({ name: 'override_program_version' });

    let context: StoreProgramContext;

    try {
        context = getStoreProgramContext();
    } catch (_) {
        context = {
            currentProgram: DEFAULT_STORE_PROGRAM,
            spendingProgress: DEFAULT_SPENDING_PROGRESS,
            showOldProgram: DEFAULT_SHOW_OLD_PROGRAM,
        };
    }

    if (programOverride) {
        context.currentProgram = programOverride;
    }

    if (programVersionOverride === 'OLD') {
        context.showOldProgram = true;
    }

    if (programVersionOverride === 'NEW') {
        context.showOldProgram = false;
    }

    context.spendingProgress = applySpendingOverrides(context.spendingProgress, programVersionOverride === 'NEW');

    return context;
};
